import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { updateMetaTags, addStructuredData, getSiteUrl } from '../../utils/seo';

const EmpresaPage = () => {
  useEffect(() => {
    const siteUrl = getSiteUrl();
    
    updateMetaTags({
      title: 'Empresa - Vaciado de Pisos El Prat | JUSTCLEAN ELPRAT',
      description: 'Más de 10 años de experiencia en vaciado de pisos y limpieza extrema en El Prat de Llobregat. Equipo profesional y servicio discreto. ☎ 602 36 59 96',
      keywords: 'empresa vaciado pisos El Prat, equipo profesional, experiencia limpieza Diógenes',
      path: '/empresa'
    });

    // Add AboutPage Schema
    const aboutSchema = addStructuredData({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "Sobre JUSTCLEAN ELPRAT",
      "description": "Más de 10 años de experiencia en vaciado de pisos y limpieza extrema en El Prat de Llobregat",
      "url": `${siteUrl}/empresa`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "JUSTCLEAN ELPRAT",
        "foundingDate": "2014",
        "description": "Empresa especializada en vaciado de pisos y locales, limpieza por síndrome de Diógenes y retirada de muebles en El Prat de Llobregat",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "Carrer de la Verge de Montserrat, 45",
          "addressLocality": "El Prat de Llobregat",
          "addressRegion": "Barcelona",
          "postalCode": "08820",
          "addressCountry": "ES"
        },
        "telephone": "+34602365996",
        "email": "info@elprat.justclean.es"
      }
    });

    return () => {
      document.head.removeChild(aboutSchema);
    };
  }, []);

  const valores = [
    {
      icon: 'ri-shield-check-line',
      title: 'Profesionalidad',
      description: 'Equipo cualificado con más de 10 años de experiencia en vaciado de pisos y locales en El Prat de Llobregat y comarca.',
    },
    {
      icon: 'ri-eye-off-line',
      title: 'Discreción',
      description: 'Especialmente importante en casos de síndrome de Diógenes. Trabajamos con total respeto y confidencialidad.',
    },
    {
      icon: 'ri-timer-line',
      title: 'Rapidez',
      description: 'Servicio urgente disponible 24 horas. Presupuesto en menos de 24h y ejecución inmediata del vaciado.',
    },
    {
      icon: 'ri-recycle-line',
      title: 'Responsabilidad',
      description: 'Gestión legal de residuos. Reciclamos y donamos todo lo aprovechable de muebles y electrodomésticos.',
    },
    {
      icon: 'ri-price-tag-3-line',
      title: 'Transparencia',
      description: 'Presupuestos claros sin sorpresas. Precio cerrado antes de iniciar cualquier trabajo de vaciado.',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Atención Personalizada',
      description: 'Cada proyecto es único. Adaptamos nuestro servicio de vaciado a las necesidades específicas de cada cliente.',
    },
  ];

  const experiencia = [
    {
      title: 'Equipo Certificado',
      description: 'Todos nuestros profesionales están formados en técnicas de vaciado, limpieza extrema y gestión de residuos.',
    },
    {
      title: 'Seguros y Garantías',
      description: 'Contamos con seguro de responsabilidad civil y garantizamos la satisfacción en todos nuestros servicios.',
    },
    {
      title: 'Equipamiento Profesional',
      description: 'Disponemos de vehículos propios, herramientas especializadas y productos de limpieza profesionales.',
    },
    {
      title: 'Experiencia Local',
      description: 'Conocemos perfectamente El Prat de Llobregat y toda la comarca del Baix Llobregat desde hace más de una década.',
    },
  ];

  return (
    <div className="min-h-screen">
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[60vh] flex items-center overflow-hidden pt-20">
          {/* Background Image with Overlay */}
          <div className="absolute inset-0 z-0">
            <img
              src="https://public.readdy.ai/ai/img_res/ea827e8400d0c9f5623047e072a35d03.jpg"
              alt="Equipo profesional JUSTCLEAN ELPRAT"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-primary/90 via-dark/85 to-dark/90"></div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10 w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="text-white">
                <div className="inline-block mb-4">
                  <span className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-xs sm:text-sm font-medium">
                    Sobre Nosotros
                  </span>
                </div>
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight break-words">
                  Expertos en Vaciado de Pisos desde 2014 en El Prat de Llobregat
                </h1>
                <p className="text-base sm:text-lg md:text-xl text-white/90 leading-relaxed">
                  Somos una empresa especializada en <strong>vaciado de pisos y locales</strong>, <strong>limpieza por síndrome de Diógenes</strong> y <strong>retirada de muebles</strong> en El Prat de Llobregat y toda la comarca del Baix Llobregat.
                </p>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 sm:p-8">
                <h2 className="text-xl sm:text-2xl font-bold mb-6 text-white">Profesionalidad y Discreción en Cada Servicio</h2>
                <div className="space-y-4 text-white/90 leading-relaxed text-sm sm:text-base">
                  <p>
                    Desde 2014, JUSTCLEAN ELPRAT se ha consolidado como referente en servicios de <strong>vaciado de pisos</strong> y <strong>limpieza extrema</strong> en El Prat de Llobregat. Nuestro equipo de profesionales cuenta con más de 10 años de experiencia ayudando a particulares, inmobiliarias y administradores de fincas.
                  </p>
                  <p>
                    Entendemos que cada situación es única, especialmente en casos de <strong>síndrome de Diógenes</strong> o <strong>suciedad extrema</strong>. Por eso ofrecemos un trato personalizado, discreto y respetuoso, garantizando la máxima confidencialidad en todos nuestros trabajos.
                  </p>
                  <p>
                    Trabajamos en todos los barrios de El Prat (Centre, Eixample, Sant Cosme, La Granja, Jardins de la Pau) y localidades cercanas como Viladecans, Gavà, Sant Boi, Cornellà, L'Hospitalet y Castelldefels.
                  </p>
                </div>
                <a
                  href="/servicios"
                  className="inline-flex items-center gap-2 bg-white text-primary px-6 py-3 rounded-full font-bold mt-6 hover:bg-opacity-90 transition-all cursor-pointer whitespace-nowrap text-sm sm:text-base"
                >
                  Ver Nuestros Servicios
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Valores Section */}
        <section className="py-20 bg-light">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-dark mb-4">
                Nuestros Valores y Compromiso
              </h2>
              <p className="text-lg text-secondary max-w-3xl mx-auto">
                Lo que nos diferencia en el sector del <strong>vaciado de pisos</strong> y <strong>limpieza extrema</strong> en El Prat de Llobregat. <a href="/servicios" className="text-primary hover:underline font-semibold">Descubre todos nuestros servicios</a>
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {valores.map((valor, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 hover:shadow-lg transition-all duration-300 group border-t-4 border-transparent hover:border-primary"
                >
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4 group-hover:bg-primary group-hover:scale-110 transition-all">
                    <i className={`${valor.icon} text-3xl text-primary group-hover:text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-dark mb-3">{valor.title}</h3>
                  <p className="text-dark/70 leading-relaxed">{valor.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Experiencia Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="w-full h-96 rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20cleaning%20team%20with%20equipment%20and%20truck%20organized%20workspace%20clean%20background%20teamwork%20service%20industry&width=800&height=600&seq=team-empresa&orientation=landscape"
                  alt="Equipo profesional JUSTCLEAN ELPRAT"
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div>
                <h2 className="text-4xl font-bold text-dark mb-6">
                  Equipo Profesional y Certificado
                </h2>
                <div className="space-y-6">
                  {experiencia.map((item, index) => (
                    <div key={index} className="flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                          <i className="ri-checkbox-circle-fill text-primary text-2xl"></i>
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-dark mb-2">{item.title}</h3>
                        <p className="text-dark/70 leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Colaboraciones Section */}
        <section className="py-20 bg-dark text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Trabajamos con Inmobiliarias y Administradores de Fincas
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto mb-10 leading-relaxed">
              Ofrecemos servicios especializados de <strong>vaciado de pisos</strong> para profesionales del sector inmobiliario en El Prat de Llobregat. Presupuestos especiales para colaboraciones continuadas.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <i className="ri-building-line text-5xl text-primary mb-4"></i>
                <h3 className="text-xl font-bold mb-2">Inmobiliarias</h3>
                <p className="text-white/80">
                  Vaciado rápido de pisos para nuevos inquilinos. Servicio express en 24-48 horas.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <i className="ri-community-line text-5xl text-primary mb-4"></i>
                <h3 className="text-xl font-bold mb-2">Administradores</h3>
                <p className="text-white/80">
                  Gestión de pisos heredados, embargos y situaciones complejas con total discreción.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <i className="ri-store-2-line text-5xl text-primary mb-4"></i>
                <h3 className="text-xl font-bold mb-2">Locales Comerciales</h3>
                <p className="text-white/80">
                  Vaciado de oficinas, tiendas y espacios comerciales. Gestión integral de residuos.
                </p>
              </div>
            </div>

            <a
              href="/contacto"
              className="inline-flex items-center gap-2 bg-primary text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-opacity-90 transition-all cursor-pointer whitespace-nowrap"
            >
              Solicitar Colaboración
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </section>

        {/* CTA Final */}
        <section className="py-16 sm:py-20 bg-light">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-dark mb-6 break-words">
              ¿Necesitas un Presupuesto para Vaciar un Piso?
            </h2>
            <p className="text-lg sm:text-xl text-secondary mb-8">
              Contacta con nosotros y te responderemos en menos de 2 horas
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+34602365996"
                className="inline-flex items-center justify-center gap-2 sm:gap-3 bg-primary text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold text-base sm:text-lg hover:bg-opacity-90 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line text-xl sm:text-2xl"></i>
                <span>602 36 59 96</span>
              </a>
              <a
                href="/contacto"
                className="inline-flex items-center justify-center gap-2 sm:gap-3 border-2 border-primary text-primary px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold text-base sm:text-lg hover:bg-primary hover:text-white transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-line text-xl sm:text-2xl"></i>
                <span>Formulario de Contacto</span>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default EmpresaPage;
